import React, { useState, useEffect, useCallback, useRef } from 'react';
import _ from 'lodash';
import { Info, X, Bold, Italic, List, Link, Loader2, Trash2 } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { useMemo } from 'react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';


import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { InfoTooltip } from "./ui/info-tooltip";
import { ImageUpload } from "./ImageUpload";
import { TagSelector } from "./TagSelector";
import { ProductBoxEditor } from "./SortableBoxContent";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";


const normalizeValue = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return _.memoize(
      (arr) => arr.map(item => {
        if (typeof item === 'object') {
          return {
            id: item.id,
            name: item.name,
            ...(item.category ? { category: item.category } : {}),
            ...(item.quantity ? { quantity: item.quantity } : {}),
            ...(item.description ? { description: item.description } : {})
          };
        }
        return item;
      }).sort((a, b) => {
        // Handle box contents comparison differently
        if (a.quantity !== undefined && b.quantity !== undefined) {
          return 0; // Preserve original order for box contents
        }
        // For other cases, compare IDs if they exist, otherwise convert to strings
        const aStr = (a.id || a)?.toString() || '';
        const bStr = (b.id || b)?.toString() || '';
        return aStr.localeCompare(bStr);
      }),
      arr => JSON.stringify(arr)
    )(value);
  }
  if (typeof value === 'string') return value.trim();
  return value;
};

// Helper function to compare two values deeply
const areValuesEqual = _.memoize(
  (val1, val2) => {
    const norm1 = normalizeValue(val1);
    const norm2 = normalizeValue(val2);
    
    if (Array.isArray(norm1) && Array.isArray(norm2)) {
      return JSON.stringify(norm1) === JSON.stringify(norm2);
    }
    
    return norm1 === norm2;
  },
  (val1, val2) => JSON.stringify(val1) + '::' + JSON.stringify(val2)
);

// Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [formats, setFormats] = useState({
    bold: false,
    italic: false
  });

  const updateFormats = () => {
    if (document.queryCommandState) {
      setFormats({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic')
      });
    }
  };

  useEffect(() => {
    const handleSelectionChange = () => {
      if (document.activeElement === editorRef.current) {
        updateFormats();
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    console.log('Effect ran with dependencies 1:', handleSelectionChange);

    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  const handleFormat = (command) => {
    document.execCommand(command, false);
    editorRef.current?.focus();
    updateFormats();
  };

  useEffect(() => {
    if (editorRef.current) {
      if (!value) {
        editorRef.current.innerHTML = '';
      } else if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
      }
    }
    console.log('Effect ran with dependencies 2', value);

  }, [value]);

  return (
    <div className="border rounded-md overflow-hidden">
      <div className="border-b bg-gray-50 p-2 flex gap-2">
        <button
          type="button"
          onClick={() => handleFormat('bold')}
          className={`p-1 rounded hover:bg-gray-200 ${
            formats.bold ? 'bg-gray-200 text-blue-600' : ''
          }`}
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat('italic')}
          className={`p-1 rounded hover:bg-gray-200 ${
            formats.italic ? 'bg-gray-200 text-blue-600' : ''
          }`}
        >
          <Italic className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat('insertUnorderedList')}
          className="p-1 rounded hover:bg-gray-200"
        >
          <List className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => {
            const url = prompt('Enter URL:');
            if (url) document.execCommand('createLink', false, url);
            editorRef.current?.focus();
          }}
          className="p-1 rounded hover:bg-gray-200"
        >
          <Link className="h-4 w-4" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        className="p-3 min-h-[150px] focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        data-placeholder={placeholder}
      />
    </div>
  );
};


const ProductForm = ({ onSave, onCancel, onChange }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  
  // State declarations
  const [isLoading, setIsLoading] = useState(!!productId);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [showImageWarning, setShowImageWarning] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [initialData, setInitialData] = useState(null);

  const [availableTags, setAvailableTags] = useState({
    compatibility_tags: [],
    simple_tags: [],
    categories: [],
    kind_of_product: []
  });

  const [isLoadingTags, setIsLoadingTags] = useState(true);

const [productData, setProductData] = useState({
  netlab_id: '',
  title: '',
  short_description: '',
  long_description: '',
  warning_description: '', // Added new field
  box_contents: [],
  sku: '',
  price: '',
  total_inventory: '',
  available_inventory: '',
  active: false,
  id: null,
  compatibility_tags: [],
  simple_tags: [],
  categories: [],
  kind_of_product: [],
  images: []
});

  // Validation functions
  const validateField = (name, value) => {
    switch (name) {
      case 'netlab_id':
        return value.trim() ? '' : 'Netlab ID is required';
      case 'short_description':
        return value.length <= 200 ? '' : 'Short description cannot exceed 200 characters';
      case 'title':
        return value.trim().length >= 3 ? '' : 'Title must contain at least 3 characters';
      case 'price':
        return value && value >= 0 ? '' : 'Price must be a positive number';
      case 'total_inventory':
        return !isNaN(value) && parseInt(value) >= 0 ? '' : 'Total inventory must be a positive number';
      case 'available_inventory':
        return !isNaN(value) && parseInt(value) >= 0 && parseInt(value) <= parseInt(productData.total_inventory) 
          ? '' : 'Available inventory must be less than or equal to total inventory';
      default:
        return '';
    }
  };

  const validateAllFields = () => {
    const requiredFields = {
      netlab_id: 'Netlab ID',
      title: 'Title',
      short_description: 'Short Description',
      sku: 'SKU',
      price: 'Price',
      total_inventory: 'Total Inventory',
      available_inventory: 'Available Inventory'
    };

    return Object.entries(requiredFields)
      .filter(([key]) => !productData[key])
      .map(([_, label]) => label);
  };

  const handleBoxContentsChange = (newContents) => {
    setProductData(prev => ({
      ...prev,
      box_contents: newContents
    }));
  };

  // Event handlers
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    if (name === 'price') {
      // Remove any non-numeric characters
      const numericValue = value.replace(/[^0-9]/g, '');
      // Store as is (in cents)
      processedValue = numericValue || '';
    } else if (name === 'total_inventory' || name === 'available_inventory') {
      const cleanValue = value.replace(/^0+/, '') || '0';
      const numValue = parseInt(cleanValue);
      
      if (name === 'available_inventory') {
        const totalInventory = parseInt(productData.total_inventory) || 0;
        processedValue = Math.min(numValue || 0, totalInventory).toString();
      } else {
        processedValue = numValue.toString();
      }
    }
  
    setProductData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  
    const error = validateField(name, processedValue);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  }, [productData.total_inventory]);

  const handleActiveChange = (checked) => {
    const missingFields = validateAllFields();
    if (checked && missingFields.length > 0) {
      setValidationMessage(`Cannot activate product. Missing fields: ${missingFields.join(', ')}`);
      setShowValidationWarning(true);
      return;
    }
    setProductData(prev => ({ ...prev, active: checked }));
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/product/${productId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      toast.success('Product deleted successfully');
      onSave?.(null);
      navigate('/products');
    } catch (error) {
      toast.error(error.message || 'Error deleting product');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleImagesUpdate = useCallback((newImages) => {
    console.log('handleImagesUpdate called with:', newImages);
    setProductData(prev => {
      // Only update if the images have actually changed
      if (isEqual(prev.images, newImages)) {
        return prev;
      }
      const newState = {
        ...prev,
        images: newImages
      };
      console.log('New productData state:', newState);
      return newState;
    });
  }, []); 
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    console.log('Starting submit with productData:', productData);
  
    // Check for netlab_id first
    if (!productData.netlab_id.trim()) {
      setValidationMessage('Netlab ID is required to save the product');
      setShowValidationWarning(true);
      setIsSaving(false);
      return;
    }
  
    // Check for unuploaded images
    const hasUnuploadedImages = productData.images.some(img => !img.url);
  
    if (hasUnuploadedImages) {
      setShowImageWarning(true);
      setIsSaving(false);
      return;
    }
  
    try {
      const endpoint = productId 
        ? `${API_BASE_URL}/api/product/${productId}`
        : `${API_BASE_URL}/api/product`;
      
      const method = productId ? 'PUT' : 'POST';
  
      // Prepare final data for submission
      const finalProductData = {
        ...productData,
        compatibility_tags: {
          existing: productData.compatibility_tags.map(tag => tag.id),
          new: []
        },
        simple_tags: {
          existing: productData.simple_tags.map(tag => tag.id),
          new: []
        },
        categories: {
          existing: productData.categories.map(tag => tag.id),
          new: []
        },
        kind_of_product: {
          existing: productData.kind_of_product.map(tag => tag.id),
          new: []
        },
        // Format box_contents as an array of objects
        box_contents: Array.isArray(productData.box_contents) 
          ? productData.box_contents 
          : [],
        images: productData.images
          .map((image, index) => ({
            url: image.url,
            order: index + 1
          }))
          .filter(img => img.url)
      };
  
      console.log('Submitting final product data:', finalProductData);
  
      const response = await fetch(endpoint, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalProductData)
      });
  
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save product');
      }
  
      const result = await response.json();
      setInitialData(productData);
      onChange?.(false);
      toast.success(productId ? 'Product updated successfully' : 'Product created successfully');
      onSave?.(result);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Error saving product');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const fetchTags = async () => {
      setIsLoadingTags(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/tags/all-tags`, {
          credentials: 'include'
        });
  
        if (!response.ok) {
          throw new Error('Failed to fetch tags');
        }
  
        const data = await response.json();
        
        setAvailableTags({
          compatibility_tags: data.compatibility_tags || [],
          simple_tags: data.simple_tags || [],
          categories: data.categories || [],
          kind_of_product: data.kind_of_product || []
        });
      } catch (error) {
        console.error('Error fetching tags:', error);
        toast.error('Error loading tags');
      } finally {
        setIsLoadingTags(false);
      }
    };
  
    fetchTags();
  }, []);

  // Load initial data
  useEffect(() => {
    const fetchProductData = async () => {
      if (!productId) {
        setIsLoading(false);
        setInitialData(productData);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/product/${productId}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch product data');
        }

        const data = await response.json();
        const formattedData = {
          netlab_id: data.netlab_id || '',
          title: data.title || '',
          short_description: data.short_description || '',
          long_description: data.long_description || '',
          warning_description: data.warning_description || '', // Add this line to load existing client warnings
          // Parse box_contents if it's a string, otherwise use as is if it's an array
          box_contents: Array.isArray(data.box_contents) 
            ? data.box_contents 
            : [],
          sku: data.sku || '',
          price: data.price ? data.price.toString() : '',
          total_inventory: data.total_inventory ? data.total_inventory.toString() : '',
          available_inventory: data.available_inventory ? data.available_inventory.toString() : '',
          active: Boolean(data.active),
          id: data.id,
          compatibility_tags: (data.compatibility_tags || []).map(tag => ({
            id: tag.id,
            name: tag.name,
            category: tag.category
          })),
          simple_tags: (data.simple_tags || []).map(tag => ({
            id: tag.id,
            name: tag.name
          })),
          categories: (data.categories || []).map(tag => ({
            id: tag.id,
            name: tag.name
          })),
          kind_of_product: (data.kind_of_product || []).map(tag => ({
            id: tag.id,
            name: tag.name
          })),
          images: (data.photos || [])
            .map(photo => ({
              id: photo.id,
              url: photo.url,
              name: `Image ${photo.order}`,
              order: photo.order
            }))
            .sort((a, b) => a.order - b.order)
        };

        setProductData(formattedData);
        setInitialData(formattedData);
      } catch (error) {
        toast.error('Error loading product data');
        console.error('Error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductData();
    console.log('Effect ran with dependencies 3:', productId);
  }, [productId]);

  const memoizedInitialData = useMemo(() => initialData, [initialData]);
  const memoizedProductData = useMemo(() => productData, [productData]);

  const debouncedOnChange = useMemo(() => debounce(onChange, 300), [onChange]);

  const fieldsToCompare = useMemo(() => [
    'netlab_id', 'title', 'short_description', 'long_description', 'warning_description', // Added new field to compare
    'box_contents', 'sku', 'price', 'total_inventory',
    'available_inventory', 'active', 'compatibility_tags',
    'simple_tags', 'categories', 'kind_of_product', 'images'
  ], []);
  
  const hasChanges = useMemo(() => {
    if (!initialData) return false;
    return fieldsToCompare.some(field =>
      !areValuesEqual(productData[field], initialData[field])
    );
  }, [initialData, productData, fieldsToCompare]);
  
  useEffect(() => {
    debouncedOnChange(hasChanges);
  }, [hasChanges, debouncedOnChange]);

  return (
    <div className="space-y-6">
      {/* Form Header with Save/Cancel */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Switch
            id="active"
            checked={productData.active}
            onCheckedChange={handleActiveChange}
            disabled={isSaving}
          />
          <Label htmlFor="active" className="text-sm font-medium">
            Active
          </Label>
          <InfoTooltip text="Product visibility in store (requires all fields to be filled)" />
        </div>
        <div className="flex items-center gap-2">
          {productId && (
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting || isSaving}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isSaving || isDeleting}
            type="button"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving || isDeleting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Product'
            )}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Identifiers Section */}
        <div className="p-4 border rounded-lg bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="netlab_id" className="text-sm font-medium flex items-center gap-2">
                Netlab ID
                <InfoTooltip text="Unique identifier from Netlab system" />
              </Label>
              <Input
                id="netlab_id"
                name="netlab_id"
                value={productData.netlab_id}
                onChange={handleChange}
                className={errors.netlab_id ? 'border-red-500' : ''}
              />
              {errors.netlab_id && 
                <p className="text-red-500 text-sm">{errors.netlab_id}</p>
              }
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="sku" className="text-sm font-medium flex items-center gap-2">
                SKU
                <InfoTooltip text="Unique product identification code" />
              </Label>
              <Input
                id="sku"
                name="sku"
                value={productData.sku}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Inventory and Price Section */}
        <div className="p-4 border rounded-lg bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="price" className="text-sm font-medium flex items-center gap-2">
                Price (€)
                <InfoTooltip text="Price in euros (will be saved in cents)" />
              </Label>
              <Input
                id="price"
                name="price"
                type="text" // Changed from "number" to "text"
                value={productData.price ? (parseInt(productData.price) / 100).toFixed(2) : ''}
                onChange={handleChange}
                className={errors.price ? 'border-red-500' : ''}
                />
            </div>

            <div className="space-y-2">
              <Label htmlFor="total_inventory" className="text-sm font-medium flex items-center gap-2">
                Total Inventory
                <InfoTooltip text="Total number of items in stock" />
              </Label>
              <Input
                id="total_inventory"
                name="total_inventory"
                type="number"
                value={productData.total_inventory}
                onChange={handleChange}
                className={errors.total_inventory ? 'border-red-500' : ''}
              />
              {errors.total_inventory && 
                <p className="text-red-500 text-sm">{errors.total_inventory}</p>
              }
            </div>

            <div className="space-y-2">
              <Label htmlFor="available_inventory" className="text-sm font-medium flex items-center gap-2">
                Available Inventory
                <InfoTooltip text="Number of items available for purchase" />
              </Label>
              <Input
                id="available_inventory"
                name="available_inventory"
                type="number"
                value={productData.available_inventory}
                onChange={handleChange}
                className={errors.available_inventory ? 'border-red-500' : ''}
              />
              {errors.available_inventory && 
                <p className="text-red-500 text-sm">{errors.available_inventory}</p>
              }
            </div>
          </div>
        </div>

        {/* Tags Section */}
        <div className="p-4 border rounded-lg bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First row - existing tags */}
            <div className="space-y-2">
              {isLoadingTags ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <TagSelector
                  title="Compatibility Tags"
                  tooltipText="Select tags indicating product compatibility"
                  selectedTags={productData.compatibility_tags}
                  onTagsChange={({existing}) => setProductData(prev => ({
                    ...prev,
                    compatibility_tags: existing
                  }))}
                  tableName="compatibility_tags"
                  mode="select"
                  availableTags={availableTags.compatibility_tags}
                />
              )}
            </div>
            <div className="space-y-2">
              {isLoadingTags ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <TagSelector
                  title="Product Tags"
                  tooltipText="Add descriptive tags to categorize the product"
                  selectedTags={productData.simple_tags}
                  onTagsChange={({existing}) => setProductData(prev => ({
                    ...prev,
                    simple_tags: existing
                  }))}
                  tableName="simple_tags"
                  mode="create"
                  availableTags={availableTags.simple_tags}
                />
              )}
            </div>

            {/* Second row - new tags */}
            <div className="space-y-2">
              {isLoadingTags ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <TagSelector
                  title="Categoria"
                  tooltipText="Seleziona la categoria principale di prodotto"
                  selectedTags={productData.categories}
                  onTagsChange={({existing}) => setProductData(prev => ({
                    ...prev,
                    categories: existing
                  }))}
                  tableName="categories"
                  mode="select"
                  availableTags={availableTags.categories}
                />
              )}
            </div>
            <div className="space-y-2">
              {isLoadingTags ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <TagSelector
                  title="Kind of Product"
                  tooltipText="Add descriptive tags to categorize the product"
                  selectedTags={productData.kind_of_product}
                  onTagsChange={({existing}) => setProductData(prev => ({
                    ...prev,
                    kind_of_product: existing
                  }))}
                  tableName="kind_of_product"
                  mode="create"
                  availableTags={availableTags.kind_of_product}
                />
              )}
            </div>
          </div>
        </div>

        {/* Description Sections */}
        <div className="space-y-6">
          <div className="p-4 border rounded-lg bg-white">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-medium flex items-center gap-2">
                Title
                <InfoTooltip text="Main product title shown to customers" />
              </Label>
              <Input
                id="title"
                name="title"
                value={productData.title}
                onChange={handleChange}
                className={errors.title ? 'border-red-500' : ''}
              />
              {errors.title && 
                <p className="text-red-500 text-sm">{errors.title}</p>
              }
            </div>
          </div>

          <div className="p-4 border rounded-lg bg-white">
            <div className="space-y-2">
              <Label htmlFor="short_description" className="text-sm font-medium flex items-center gap-2">
                Short Description
                <InfoTooltip text="Brief product description for listings (max 200 characters)" />
              </Label>
              <div className="relative">
                <Input
                  id="short_description"
                  name="short_description"
                  value={productData.short_description}
                  onChange={handleChange}
                  maxLength={200}
                />
                <span className="absolute right-2 bottom-2 text-sm text-gray-400">
                  {productData.short_description.length}/200
                </span>
              </div>
              {errors.short_description && 
                <p className="text-red-500 text-sm">{errors.short_description}</p>
              }
            </div>
          </div>

          <div className="p-4 border rounded-lg bg-white">
            <div className="space-y-2">
              <Label htmlFor="long_description" className="text-sm font-medium flex items-center gap-2">
                Detailed Description
                <InfoTooltip text="INserisci qui le informazioni del prodotto nel dettaglio" />
              </Label>
              <RichTextEditor
                value={productData.long_description}
                onChange={(value) => setProductData(prev => ({ ...prev, long_description: value }))}
                placeholder="Inserisci la descrizione dettagliata..."
              />
            </div>
          </div>

          {/* New field: Avvertenze per il cliente */}
          <div className="p-4 border rounded-lg bg-white">
            <div className="space-y-2">
              <Label htmlFor="warning_description" className="text-sm font-medium flex items-center gap-2">
                Avvertenze per il Cliente
                <InfoTooltip text="Inserisci gli avvisi e le informazioni importanti per il cliente che distingono questo prodotto dagli altri" />
              </Label>
              <RichTextEditor
                value={productData.warning_description}
                onChange={(value) => setProductData(prev => ({ ...prev, warning_description: value }))}
                placeholder="Inserisci gli avvisi e le informazioni importanti..."
              />
            </div>
          </div>

          <div className="p-4 border rounded-lg bg-white">
            <div className="space-y-2">
              <Label 
                htmlFor="box_contents" 
                className="text-sm font-medium flex items-center gap-2"
              >
                Box Contents
                <InfoTooltip text="List of items included in the product package" />
              </Label>
              <ProductBoxEditor
                initialData={productData.box_contents}
                onChange={handleBoxContentsChange}
              />
            </div>
          </div>
        </div>

        {/* Images Section */}
        <div className="border rounded-lg bg-white"> {/* Removed p-4 from here */}
          <div className="space-y-2 p-4 pb-0"> {/* Added padding here for the label only */}
            <Label htmlFor="images" className="text-sm font-medium flex items-center gap-2">
              Product Images
              <InfoTooltip text="Carica ed organizza qui le immagini prodotto" />
            </Label>
          </div>
          <ImageUpload 
            initialImages={productData.images}
            onImagesUpdate={handleImagesUpdate}
            uploadEndpoint="/api/image/upload"
            maxImages={10}
          />
        </div>
      </div>

      {/* Alert Dialogs */}
      <AlertDialog open={showValidationWarning} onOpenChange={setShowValidationWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Warning</AlertDialogTitle>
            <AlertDialogDescription>
              {validationMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowValidationWarning(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showImageWarning} onOpenChange={setShowImageWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Warning</AlertDialogTitle>
            <AlertDialogDescription>
              Please confirm all image uploads before saving the product.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowImageWarning(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this product? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductForm;
